import * as THREE from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import type { ToolAnimation } from '../game/interaction';
import { Templates as T } from './GeometryBuilder';
import { toon, toonUnique } from './materials';

const SKIN = '#ffd9b8';
const HAIR = '#8a5a3c';
const BOOT_COLORS = ['#7a4a2a', '#5fb4ff', '#ff8fab'];
const ACTION_DURATION = 0.5;
const CHEEK_MATERIAL = toonUnique({ color: '#ff9fb5', transparent: true, opacity: 0.8 });

const capsule = (radius: number, length: number) =>
  new THREE.CapsuleGeometry(radius, length, 4, 10);

/**
 * Geometry shared by every character, so visitors coming and going never leak
 * GPU memory. Only the outfit materials are per-character (they're recoloured).
 */
const SHARED = {
  leg: capsule(0.085, 0.14),
  torso: capsule(0.25, 0.18),
  sleeve: capsule(0.065, 0.16),
  smile: new THREE.TorusGeometry(0.035, 0.008, 4, 10, Math.PI),
  crown: new THREE.CylinderGeometry(0.2, 0.24, 0.18, 12),
  band: new THREE.CylinderGeometry(0.245, 0.245, 0.05, 12),
  shadow: new THREE.CircleGeometry(0.42, 20),
  shadowMaterial: new THREE.MeshBasicMaterial({
    color: '#000000',
    transparent: true,
    opacity: 0.16,
    depthWrite: false,
  }),
};

/**
 * A chibi farmer built from primitives. Shared by the local player and every
 * remote visitor; only the outfit colour differs.
 */
export class Character {
  readonly root = new THREE.Group();
  private readonly body = new THREE.Group();
  private readonly head = new THREE.Group();
  private readonly armL = new THREE.Group();
  private readonly armR = new THREE.Group();
  private readonly legL = new THREE.Group();
  private readonly legR = new THREE.Group();
  private readonly can = new THREE.Group();
  private readonly outfit: THREE.MeshToonMaterial;
  private readonly ribbon: THREE.MeshToonMaterial;
  private readonly boots: THREE.MeshToonMaterial;
  private readonly label: HTMLDivElement;
  private readonly bubble: HTMLDivElement;
  private readonly bubbleObject: CSS2DObject;

  private walkPhase = 0;
  private action: ToolAnimation = 'none';
  private actionTime = 0;
  private bubbleTimer = 0;
  private blinkTimer = 2;
  private readonly eyes: THREE.Mesh[] = [];

  constructor(color: string, name: string | null = null) {
    this.outfit = toonUnique({ color });
    this.ribbon = toonUnique({ color });
    this.boots = toonUnique({ color: BOOT_COLORS[0] });

    const shirt = toon('#fffaf2');
    const skin = toon(SKIN);

    // Legs pivot at the hips.
    for (const [leg, x] of [
      [this.legL, -0.12],
      [this.legR, 0.12],
    ] as const) {
      leg.position.set(x, 0.32, 0);
      const limb = new THREE.Mesh(SHARED.leg, this.outfit);
      limb.position.y = -0.12;
      const boot = new THREE.Mesh(T.sphere, this.boots);
      boot.scale.set(0.11, 0.08, 0.14);
      boot.position.set(0, -0.26, 0.03);
      leg.add(limb, boot);
      this.root.add(leg);
    }

    // Torso: overalls over a shirt.
    const torso = new THREE.Mesh(SHARED.torso, this.outfit);
    torso.position.y = 0.55;
    const shirtTop = new THREE.Mesh(T.sphere, shirt);
    shirtTop.scale.set(0.24, 0.16, 0.22);
    shirtTop.position.set(0, 0.74, 0);
    const pocket = new THREE.Mesh(T.box, toon('#ffffff'));
    pocket.scale.set(0.16, 0.1, 0.02);
    pocket.position.set(0, 0.56, 0.25);
    this.body.add(torso, shirtTop, pocket);

    for (const [arm, x] of [
      [this.armL, -0.29],
      [this.armR, 0.29],
    ] as const) {
      arm.position.set(x, 0.72, 0);
      const sleeve = new THREE.Mesh(SHARED.sleeve, shirt);
      sleeve.position.y = -0.13;
      const hand = new THREE.Mesh(T.sphere, skin);
      hand.scale.setScalar(0.07);
      hand.position.y = -0.28;
      arm.add(sleeve, hand);
      this.body.add(arm);
    }

    // Watering can held in the right hand while watering.
    const canBody = new THREE.Mesh(T.cylinder, toon('#7cc6fe'));
    canBody.scale.set(0.11, 0.18, 0.11);
    const spout = new THREE.Mesh(T.cylinder, toon('#7cc6fe'));
    spout.scale.set(0.025, 0.22, 0.025);
    spout.rotation.x = 1.0;
    spout.position.set(0, 0.1, 0.05);
    this.can.add(canBody, spout);
    this.can.position.set(0, -0.36, 0.08);
    this.can.visible = false;
    this.armR.add(this.can);

    // Head with big eyes, rosy cheeks and a straw hat.
    this.head.position.y = 1.02;
    const face = new THREE.Mesh(T.sphere, skin);
    face.scale.set(0.31, 0.29, 0.29);
    const hair = new THREE.Mesh(T.sphere, toon(HAIR));
    hair.scale.set(0.32, 0.27, 0.3);
    hair.position.set(0, 0.04, -0.04);
    this.head.add(face, hair);
    for (const x of [-0.1, 0.1]) {
      const eye = new THREE.Mesh(T.sphere, toon('#2b2340'));
      eye.scale.set(0.035, 0.05, 0.02);
      eye.position.set(x, 0.0, 0.27);
      const shine = new THREE.Mesh(T.sphere, toon('#ffffff'));
      shine.scale.set(0.3, 0.3, 0.5);
      shine.position.set(0.3, 0.35, 0.6);
      eye.add(shine);
      this.eyes.push(eye);
      const cheek = new THREE.Mesh(T.sphere, CHEEK_MATERIAL);
      cheek.scale.set(0.05, 0.03, 0.02);
      cheek.position.set(x * 1.65, -0.07, 0.255);
      this.head.add(eye, cheek);
    }
    const smile = new THREE.Mesh(SHARED.smile, toon('#8a3b3b'));
    smile.rotation.z = Math.PI;
    smile.position.set(0, -0.08, 0.28);
    this.head.add(smile);

    const brim = new THREE.Mesh(T.cylinder, toon('#f2cf63'));
    brim.scale.set(0.46, 0.035, 0.46);
    brim.position.y = 0.18;
    brim.rotation.x = -0.12;
    const crown = new THREE.Mesh(SHARED.crown, toon('#f7dc86'));
    crown.position.y = 0.3;
    crown.rotation.x = -0.12;
    const band = new THREE.Mesh(SHARED.band, this.ribbon);
    band.position.y = 0.24;
    band.rotation.x = -0.12;
    const flower = new THREE.Mesh(T.sphere, toon('#ffffff'));
    flower.scale.setScalar(0.06);
    flower.position.set(0.2, 0.27, 0.1);
    const flowerCenter = new THREE.Mesh(T.sphere, toon('#ffd23f'));
    flowerCenter.scale.setScalar(0.5);
    flowerCenter.position.z = 0.6;
    flower.add(flowerCenter);
    this.head.add(brim, crown, band, flower);
    this.body.add(this.head);
    this.root.add(this.body);

    this.root.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) o.castShadow = true;
    });

    // Soft blob shadow so the character stays grounded even without shadow maps.
    const blob = new THREE.Mesh(SHARED.shadow, SHARED.shadowMaterial);
    blob.rotation.x = -Math.PI / 2;
    blob.position.y = 0.03;
    this.root.add(blob);

    this.label = document.createElement('div');
    this.label.className = 'nametag';
    const labelObject = new CSS2DObject(this.label);
    labelObject.position.set(0, 1.85, 0);
    this.root.add(labelObject);
    this.setName(name);

    this.bubble = document.createElement('div');
    this.bubble.className = 'speech-bubble';
    this.bubbleObject = new CSS2DObject(this.bubble);
    this.bubbleObject.position.set(0, 2.25, 0);
    this.bubbleObject.visible = false;
    this.root.add(this.bubbleObject);
  }

  setName(name: string | null): void {
    this.label.textContent = name ?? '';
    this.label.style.display = name ? '' : 'none';
  }

  setColor(color: string): void {
    this.outfit.color.set(color);
    this.ribbon.color.set(color);
  }

  setBoots(level: number): void {
    this.boots.color.set(BOOT_COLORS[Math.min(level, BOOT_COLORS.length - 1)] ?? BOOT_COLORS[0]!);
  }

  /** Shows a speech bubble (emote or chat) above the head. */
  say(text: string, seconds = 4): void {
    this.bubble.textContent = text;
    this.bubble.classList.toggle('emote', [...text].length <= 2);
    this.bubbleObject.visible = true;
    this.bubbleTimer = seconds;
    // Restart the pop-in animation.
    this.bubble.classList.remove('pop');
    void this.bubble.offsetWidth;
    this.bubble.classList.add('pop');
  }

  playAction(action: ToolAnimation): void {
    if (action === 'none') return;
    this.action = action;
    this.actionTime = 0;
    this.can.visible = action === 'water';
  }

  /** @param speed normalised walking speed (0 = idle, 1 = full speed). */
  update(dt: number, speed: number, time: number): void {
    this.walkPhase += dt * (4 + speed * 7) * (speed > 0.05 ? 1 : 0);
    const swing = Math.sin(this.walkPhase) * 0.7 * Math.min(1, speed * 1.5);

    this.legL.rotation.x = swing;
    this.legR.rotation.x = -swing;
    this.armL.rotation.x = -swing * 0.8;
    this.armR.rotation.x = swing * 0.8;
    this.armL.rotation.z = -0.12;
    this.armR.rotation.z = 0.12;

    const bob = Math.abs(Math.sin(this.walkPhase)) * 0.07 * Math.min(1, speed * 2);
    const breathe = Math.sin(time * 2.2) * 0.015;
    this.body.position.y = bob;
    this.body.scale.set(1 - breathe * 0.5, 1 + breathe, 1 - breathe * 0.5);
    this.head.rotation.z = Math.sin(time * 1.3) * 0.04;
    this.body.rotation.x = 0;

    if (this.action !== 'none') {
      this.actionTime += dt;
      const t = Math.min(1, this.actionTime / ACTION_DURATION);
      const arc = Math.sin(t * Math.PI);
      switch (this.action) {
        case 'harvest':
          this.body.position.y += arc * 0.35;
          this.armL.rotation.x = this.armR.rotation.x = -2.6 * arc;
          this.armL.rotation.z = -0.4 * arc;
          this.armR.rotation.z = 0.4 * arc;
          break;
        case 'plant':
          this.body.scale.y *= 1 - arc * 0.18;
          this.body.rotation.x = arc * 0.35;
          this.armL.rotation.x = this.armR.rotation.x = -1.2 * arc;
          break;
        case 'water':
          this.body.rotation.x = arc * 0.15;
          this.armR.rotation.x = -1.3 * arc;
          this.can.rotation.x = arc * 0.9;
          break;
        case 'dig':
          this.body.rotation.x = arc * 0.4;
          this.armL.rotation.x = this.armR.rotation.x = -1.8 + arc * 1.6;
          break;
      }
      if (t >= 1) {
        this.action = 'none';
        this.can.visible = false;
      }
    }

    // Blink every few seconds.
    this.blinkTimer -= dt;
    const blinking = this.blinkTimer < 0.12;
    if (this.blinkTimer < 0) this.blinkTimer = 2 + Math.random() * 3;
    for (const eye of this.eyes) eye.scale.y = blinking ? 0.008 : 0.05;

    if (this.bubbleTimer > 0) {
      this.bubbleTimer -= dt;
      if (this.bubbleTimer <= 0) this.bubbleObject.visible = false;
    }
  }

  dispose(): void {
    this.label.remove();
    this.bubble.remove();
    this.root.removeFromParent();
    this.outfit.dispose();
    this.ribbon.dispose();
    this.boots.dispose();
  }
}
